import { z } from 'zod'

import { isoDateTime } from '../lib/date'
import { clinicIdSchema } from './clinic'

/** Calculates the line total in minor currency units, rounding once after discounting. */
export function calculateOrderTotal({
  unitPrice,
  quantity,
  discountBps,
}: Pick<Order, 'unitPrice' | 'quantity' | 'discountBps'>): number {
  return Math.round((unitPrice * quantity * (10000 - discountBps)) / 10000)
}

export const orderStatusSchema = z.enum([
  'pending',
  'prepared',
  'collected',
  'administered',
  'out_of_stock',
  'returned',
  'cancelled',
])
export type OrderStatus = z.infer<typeof orderStatusSchema>

export const paymentStatusSchema = z.enum(['unpaid', 'free', 'paid', 'failed'])
export type PaymentStatus = z.infer<typeof paymentStatusSchema>

/** A compound predicate describing a set of orders by their status and payment state. */
export type OrderPredicate = {
  readonly status: readonly OrderStatus[]
  readonly paymentStatus?: readonly PaymentStatus[]
}

/** Named lifecycle groups based on order status and, where applicable, payment status. */
export const ORDER_PREDICATES = {
  /** Has an outstanding balance that can be invoiced. */
  invoiceable: {
    status: ['prepared', 'collected', 'administered'],
    paymentStatus: ['unpaid', 'failed'],
  },
  /** Collected/administered and fully paid (or free). */
  fulfilled: { status: ['collected', 'administered'], paymentStatus: ['paid', 'free'] },
  /** Still requires an operational outcome. */
  active: { status: ['pending', 'prepared', 'out_of_stock'] },
  /** Supply is in flight or dispensed, regardless of payment status. */
  claimed: { status: ['pending', 'prepared', 'out_of_stock', 'collected', 'administered'] },
  /** Can be cancelled — pending/out-of-stock with no payment activity. */
  cancellable: { status: ['pending', 'out_of_stock'], paymentStatus: ['unpaid', 'free'] },
  /**
   * Can be un-prepared — prepared and not yet successfully settled. Includes
   * `failed` so meds can be returned to stock after a declined charge; the
   * failed payment reference is preserved so a later re-checkout voids it.
   */
  undoPrepare: { status: ['prepared'], paymentStatus: ['unpaid', 'free', 'failed'] },
} as const satisfies Record<string, OrderPredicate>

export const orderUrgencySchema = z.enum(['low', 'medium', 'high'])
export type OrderUrgency = z.infer<typeof orderUrgencySchema>

export const orderSchema = z.object({
  id: z.uuid(),
  patientId: z.uuid(),
  treatmentId: z.uuid().nullable(),
  consultationId: z.uuid().nullable(),
  productId: z.uuid(),
  /** Quantity prescribed or dispensed. May be fractional (e.g. 1.5 tablets). */
  quantity: z.number(),
  /** Customer-facing price per dispensable unit, in pence. Snapshotted at order creation. */
  unitPrice: z.number().int(),
  /** Discount applied to the line total, in basis points (0–10000). */
  discountBps: z.number().int(),
  /** Final line total after discount, in pence (rounded). Snapshotted at order creation. */
  totalPrice: z.number().int(),
  status: orderStatusSchema,
  /** When the order was most recently made ready for collection or administered. */
  preparedAt: isoDateTime.nullable(),
  /** Staff member who most recently prepared the order. */
  preparedBy: z.uuid().nullable(),
  memberNotifiedAt: isoDateTime.nullable(),
  /** Operational urgency — only set for pending medications, null for vaccines/services/consumables. */
  urgency: orderUrgencySchema.nullable(),
  clinicId: clinicIdSchema,
  /** External payment reference (e.g. Stripe PaymentIntent or Invoice ID). Set alongside paymentStatus. */
  paymentId: z.string().nullable(),
  /** Payment lifecycle — `unpaid` (awaiting payment) or `free` (zero-price order, no payment needed). */
  paymentStatus: paymentStatusSchema,
  /** When the order was physically dispensed (administered or handed to client). */
  dispensedAt: isoDateTime.nullable(),
  /** Staff member who performed the dispensation. */
  dispensedBy: z.uuid().nullable(),
  notes: z.string().nullable(),
  createdAt: isoDateTime,
  updatedAt: isoDateTime,
})

export type Order = z.output<typeof orderSchema>

/** Backend-computed derived fields added to orders in API responses. */
export type OrderDerivedFields = {
  /** True when this order is a repeat fill — i.e. not the first order on its treatment. */
  isRefill: boolean
}

/** Checks whether an order matches a compound order predicate. */
export function matchesPredicate(
  order: { status: string; paymentStatus: string },
  predicate: OrderPredicate
): boolean {
  if (!predicate.status.includes(order.status as OrderStatus)) return false
  if (
    predicate.paymentStatus &&
    !predicate.paymentStatus.includes(order.paymentStatus as PaymentStatus)
  )
    return false
  return true
}

/**
 * Returns the most relevant order for display — prefers non-cancelled/returned orders,
 * taking the most recently created among those. Falls back to the latest overall if all
 * are terminal.
 */
export function getPrimaryOrder<T extends { status: OrderStatus; createdAt: string | Date }>(
  orders: T[]
): T | null {
  const newestFirst = [...orders].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  )
  return (
    newestFirst.find((o) => o.status !== 'cancelled' && o.status !== 'returned') ??
    newestFirst[0] ??
    null
  )
}
